import fetchPublicOrderQuote from '@lyra/core/api/fetchPublicOrderQuote'
import generateNonce from '@lyra/core/utils/generateNonce'
import { OrderQuote, OrderQuoteParams } from '@lyra/web/constants/order'
import { useEffect, useMemo, useRef } from 'react'
import useSWR from 'swr'

import { roundDownToStep, roundUpToStep } from '../utils/number'
import { getDefaultOrderExpiryDays, getSignatureExpirySecsForExpiryDays } from '../utils/order'
import useSubaccount from './useSubaccount'
import useTicker from './useTicker'

export type OrderQuoteHookParams = OrderQuoteParams & {
  expiryDays: number | null
}

const fetcher = async (subaccountId: number, quoteHookParams: OrderQuoteHookParams) => {
  const { expiryDays: _expiryDays, ...quoteParams } = quoteHookParams
  const expiryDays = _expiryDays ? _expiryDays : getDefaultOrderExpiryDays(quoteParams)
  const signature_expiry_sec = getSignatureExpirySecsForExpiryDays(expiryDays)

  const { result: quote } = await fetchPublicOrderQuote({
    ...quoteParams,
    max_fee: '0',
    subaccount_id: subaccountId,
    nonce: generateNonce(),
    // public/order_quote does not require signature
    signature: '',
    signer: '',
    signature_expiry_sec,
  })

  return quote
}

export default function useOrderQuote(params: OrderQuoteHookParams) {
  const { subaccount } = useSubaccount()

  const ticker = useTicker(params.instrument_name)
  const tickStep = ticker ? +ticker.tick_size : 0

  const { limit_price, ...otherParams } = params
  const roundedLimitPrice =
    ticker && ticker.instrument_type !== 'option'
      ? // tick rounded up for BUY and down if SELL by 100 to prevent excessive refetches
        otherParams.direction === 'buy'
        ? roundUpToStep(+limit_price, tickStep * 100)
        : roundDownToStep(+limit_price, tickStep * 100)
      : limit_price

  // when paramsKey string updates, order_quote is refetched
  const paramsKey = JSON.stringify({
    ...otherParams,
    limit_price: roundedLimitPrice.toString(),
  })

  const shouldSkipQuote =
    !ticker ||
    +params.amount === 0 ||
    +params.limit_price === 0 ||
    (!!params.trigger_type && (!params.trigger_price || +params.trigger_price === 0))

  const {
    data,
    isLoading: _isLoading,
    error: _error,
  } = useSWR<OrderQuote | null, Error>(
    ['QuoteOrder', paramsKey, subaccount?.subaccount_id],
    // note: pass full params into fetcher
    () => (subaccount && !shouldSkipQuote ? fetcher(subaccount.subaccount_id, params) : null),
    {
      refreshInterval: 60 * 1000, // refresh every 1 minute
      keepPreviousData: true,
    }
  )

  const errorRef = useRef(_error)
  useEffect(() => {
    if (_error) {
      errorRef.current = _error
    }
  }, [_error])

  const error = !data ? errorRef.current : null

  // // only flag loading state if there is no data or error
  const isLoading = !ticker || (_isLoading && !data && !error && !shouldSkipQuote)

  return useMemo(() => ({ data, isLoading, error }), [data, isLoading, error])
}
